/*
 * DaisyBee Arts — product form
 *
 * Two jobs: keep the quantity stepper working, and keep the hidden variant
 * select in step with the option radios so the right variant reaches the
 * cart and the price on screen matches it.
 *
 * The select is the source of truth rather than a JS variant map, because it
 * is what the form actually posts. Without JS the form still submits the
 * selected option, which is the first available variant.
 */

const forms = document.querySelectorAll('[data-db-product-form]');

for (const form of forms) {
  const select = form.querySelector('[data-db-variant-select]');
  const qtyInput = form.querySelector('[data-db-qty-input]');
  // Look inside the form first (the commission form keeps its total there),
  // then fall back to the product page's buy column.
  const priceEl =
    form.querySelector('[data-db-price]') ||
    form.closest('.db-pdp__info')?.querySelector('[data-db-price]');
  const addLabel = form.querySelector('[data-db-add-label]');

  // Quantity stepper
  for (const button of form.querySelectorAll('[data-db-qty]')) {
    button.addEventListener('click', () => {
      if (!qtyInput) return;
      const step = Number(button.dataset.dbQty);
      const next = Math.max(1, (Number(qtyInput.value) || 1) + step);
      qtyInput.value = String(next);
    });
  }

  if (!select) continue;

  // Every combination that exists, as arrays of option values
  const combos = [...select.options].map((option) => (option.dataset.options || '').split('||'));

  /*
   * Not every painting comes in every size, so each option after the first
   * offers only the values that exist with the choices before it. Without
   * this, picking Beach Ballroom then A3 matched no variant, the lookup gave
   * up, and the previously selected painting quietly stayed in the form.
   * Returns the value the control ends up on.
   */
  const limitChoices = (position, earlier) => {
    const allowed = new Set(
      combos
        .filter((combo) => earlier.every((value, index) => combo[index] === value))
        .map((combo) => combo[position - 1])
    );

    const menu = form.querySelector('select[name="option' + position + '"]');
    if (menu) {
      for (const option of menu.options) {
        const missing = !allowed.has(option.value);
        option.hidden = missing;
        option.disabled = missing;
      }
      if (!allowed.has(menu.value)) {
        const first = [...menu.options].find((option) => !option.disabled);
        if (first) menu.value = first.value;
      }
      return menu.value;
    }

    const radios = [...form.querySelectorAll('input[name="option' + position + '"]')];
    if (radios.length === 0) return undefined;
    for (const radio of radios) {
      const missing = !allowed.has(radio.value);
      radio.disabled = missing;
      const label = radio.closest('label');
      if (label) label.hidden = missing;
    }
    let checked = radios.find((radio) => radio.checked && !radio.disabled);
    if (!checked) {
      checked = radios.find((radio) => !radio.disabled);
      if (checked) checked.checked = true;
    }
    return checked?.value;
  };

  const syncVariant = () => {
    /*
     * Read one value per option position, from whichever control that option
     * uses. This has to cope with both, because the painting list is a select
     * and the frame is a pair of radios.
     *
     * The previous version asked for [data-db-option]:checked, which a select
     * can never satisfy, so the painting was missing from the lookup, nothing
     * ever matched, and neither the price nor the variant changed.
     */
    const chosen = [];
    for (let position = 1; position <= 3; position += 1) {
      const value = limitChoices(position, chosen);
      if (value !== undefined) chosen.push(value);
    }
    if (chosen.length === 0) return;

    const wanted = chosen.join('||');
    const match = [...select.options].find((option) => option.dataset.options === wanted);
    if (!match) return;

    select.value = match.value;

    if (priceEl && match.dataset.price) priceEl.textContent = match.dataset.price;

    // A disabled option is a variant that exists but is out of stock
    const soldOut = match.disabled;
    const button = form.querySelector('.db-pdp__add');
    if (button) button.disabled = soldOut;
    if (addLabel && soldOut) addLabel.textContent = 'Sold out';
  };

  for (const input of form.querySelectorAll('[data-db-option]')) {
    input.addEventListener('change', syncVariant);
  }

  syncVariant();
}

/*
 * Framed view toggle
 *
 * Cross fades between the artwork and the framed photograph. Both are already
 * in the DOM, so this only moves a class; the fade itself is CSS. aria-pressed
 * carries the state for assistive tech, and aria-hidden keeps the inactive
 * view out of the accessibility tree so the same painting is not announced
 * twice.
 */
for (const media of document.querySelectorAll('[data-db-frame-toggle]')) {
  const button = media.querySelector('[data-db-frame-button]');
  const label = media.querySelector('[data-db-frame-label]');
  const views = [...media.querySelectorAll('[data-db-view]')];
  if (!button || views.length < 2) continue;

  const labels = {
    art: button.dataset.labelShow || label?.textContent.trim() || 'See it on a wall',
    framed: button.dataset.labelHide || 'See the artwork',
  };

  let showing = 'art';

  button.addEventListener('click', () => {
    showing = showing === 'art' ? 'framed' : 'art';

    for (const view of views) {
      const active = view.dataset.dbView === showing;
      view.classList.toggle('is-active', active);
      view.setAttribute('aria-hidden', active ? 'false' : 'true');
    }

    button.setAttribute('aria-pressed', showing === 'framed' ? 'true' : 'false');
    if (label) label.textContent = showing === 'framed' ? labels.framed : labels.art;
  });
}

/*
 * Commission checkout
 *
 * The commission form adds a real product to the basket and should land the
 * customer on checkout. Left to a native form post, Shopify honours its own
 * redirect and drops them on /cart instead, which reads as "nothing happened"
 * after paying attention to a form. So the submit is handled here: post to
 * cart/add.js, then send them to checkout.
 *
 * The form still works with JavaScript off, it just ends on the cart page,
 * which is why the markup keeps its action and return_to.
 */
for (const form of document.querySelectorAll('[data-db-commission-form]')) {
  const button = form.querySelector('button[type="submit"]');
  const label = form.querySelector('[data-db-add-label]');
  const errorBox = form.querySelector('[data-db-commission-error]');
  const original = label?.textContent.trim();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (errorBox) errorBox.hidden = true;
    if (button) button.disabled = true;
    if (label) label.textContent = form.dataset.busyLabel || 'Just a moment…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!response.ok) {
        // Shopify explains itself in `description`; anything else is unexpected
        const problem = await response.json().catch(() => ({}));
        throw new Error(problem.description || 'That did not go through. Please try again.');
      }

      window.location.assign('/checkout');
    } catch (error) {
      if (button) button.disabled = false;
      if (label) label.textContent = original || 'Pay and Start My Commission';
      if (errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      }
    }
  });
}

/*
 * Painting picker
 *
 * All the prints live on one product, so choosing a painting has to change
 * the picture as well as the variant. Both views get their src swapped from
 * a map rendered by Liquid, rather than stacking a dozen images that would
 * all have to download before the page settled.
 *
 * The framed square and the edge-to-edge scan need different framing rules,
 * so the natural flag rides along with each entry.
 */
for (const media of document.querySelectorAll('[data-db-frame-toggle]')) {
  const mapEl = media.querySelector('[data-db-art-map]');
  const artImg = media.querySelector('[data-db-art-img]');
  const framedImg = media.querySelector('[data-db-framed-img]');
  if (!mapEl) continue;

  let map = {};
  try {
    map = JSON.parse(mapEl.textContent);
  } catch {
    continue;
  }

  const form = media.closest('.db-pdp__grid')?.querySelector('[data-db-product-form]');
  if (!form) continue;

  const section = media.closest('[data-section-id]');

  const paint = () => {
    // The painting is whichever control drives option1
    const control = form.querySelector('[name="option1"]:checked, select[name="option1"]');

    // The summary beside the button and the full story below follow along
    if (section && control) {
      for (const story of section.querySelectorAll('[data-db-painting]')) {
        story.hidden = story.dataset.dbPainting !== control.value;
      }
    }

    const entry = map[control?.value];
    if (!entry) return;

    if (artImg && entry.art) {
      artImg.src = entry.art;
      artImg.removeAttribute('srcset');
      // The width and height from Liquid describe the first painting; drop
      // them so the new one takes its own shape rather than the old one's
      artImg.removeAttribute('width');
      artImg.removeAttribute('height');
      artImg.alt = control.value;
      artImg.closest('.db-frame')?.classList.toggle('db-frame--natural', Boolean(entry.natural));
    }

    if (framedImg && entry.framed) {
      framedImg.src = entry.framed;
      framedImg.removeAttribute('srcset');
      framedImg.alt = `${control.value}, shown on a wall`;
    }
  };

  for (const control of form.querySelectorAll('[name="option1"]')) {
    control.addEventListener('change', paint);
  }

  paint();
}
